/**
 * ClientSelect — автокомплит выбора клиента (centrika-docs!45, `page.team-training-journal`
 * → «Выбор клиента»): команда ищет клиента по части наименования ИЛИ ИНН без учёта регистра.
 *
 * Отличие от `EmployeeSelect`/`PositionSelect` — ПОИСК СЕРВЕРНЫЙ: клиентов может быть больше,
 * чем помещается в один ответ, поэтому фильтровать загруженный список нельзя (иначе поиск
 * находил бы только среди первых 100). Запрос уходит в `GET /api/team/clients?q=` с дебаунсом;
 * пустое поле показывает первые 100 клиентов от недавно созданных к более ранним (порядок
 * задаёт сервер), результат поиска — тоже не более 100.
 *
 * Ввод текста клиента НЕ выбирает: значение поля меняется только выбором строки из списка —
 * журнал грузится по выбранному клиенту, а не по набранному тексту (требование).
 *
 * Клавиатура и портал — по образцу `EmployeeSelect` (там же обоснование `pointer-events`,
 * preventDefault на mousedown и перехвата Escape).
 */
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { teamApi, type ClientSummary } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { cn } from '@/lib/utils'
import { Input } from '@/design-system/components/primitives/input'
import { useWorkScaleClass } from '@/design-system/work-scale'
import { useComboboxEscape } from './use-combobox-escape'

/** Текст пустого результата поиска (centrika-docs!45, дословно). */
export const CLIENT_SELECT_EMPTY = 'Клиенты не найдены'

/** Сколько клиентов показываем в списке — и без запроса, и в результатах поиска. */
export const CLIENT_SELECT_LIMIT = 100

export interface ClientSelectProps {
  /** Текущее значение — id выбранного клиента (пустая строка, пока не выбран). */
  value: string
  onSelect: (client: ClientSummary) => void
  /** Метка выбранного клиента, если его нет среди загруженных вариантов. */
  valueLabel?: string
  placeholder?: string
  id?: string
  'aria-label'?: string
  disabled?: boolean
}

export function ClientSelect({
  value,
  onSelect,
  valueLabel,
  placeholder = 'Выберите клиента',
  id,
  disabled,
  ...aria
}: ClientSelectProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [items, setItems] = useState<ClientSummary[]>([])
  // Последний ВЫБРАННЫЙ клиент: список вариантов перезагружается при каждом раскрытии, и
  // выбранного в нём может не оказаться (он не входит в первые 100 или не подходит под
  // новый запрос) — тогда поле показало бы placeholder при непустом значении.
  const [committed, setCommitted] = useState<ClientSummary | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [active, setActive] = useState(-1)
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const anchorRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null)
  const reactId = useId()
  const listboxId = `${reactId}-listbox`
  const rowId = (i: number) => `${reactId}-opt-${i}`

  useEffect(() => () => clearTimeout(blurTimer.current ?? undefined), [])

  // Серверный поиск с дебаунсом: пустой запрос — первые CLIENT_SELECT_LIMIT клиентов
  // (порядок «новые сверху» задаёт сервер), непустой — не более стольких же совпадений.
  useEffect(() => {
    if (!open) return
    const controller = new AbortController()
    const q = query.trim()
    const load = () => {
      setLoading(true)
      setError(null)
      teamApi
        .listClients({ q: q || undefined, limit: CLIENT_SELECT_LIMIT }, controller.signal)
        .then((res) => {
          if (!controller.signal.aborted) setItems(res.items)
        })
        .catch((err) => {
          if (!controller.signal.aborted) {
            setItems([])
            setError(getErrorMessage(err, 'Не удалось загрузить список клиентов'))
          }
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false)
        })
    }
    // Пустой запрос применяем сразу (раскрытие поля), набор — после паузы.
    if (q === '') {
      load()
      return () => controller.abort()
    }
    const t = setTimeout(load, 300)
    return () => {
      clearTimeout(t)
      controller.abort()
    }
  }, [open, query])

  // Подсветка не «залипает» вне диапазона при смене запроса/списка.
  useEffect(() => {
    setActive(-1)
  }, [query, open, items])

  const selected = items.find((c) => c.id === value) ?? (committed?.id === value ? committed : undefined)
  const display = selected?.name ?? valueLabel ?? ''
  const inputValue = open ? query : display
  const visible = open && !disabled

  useLayoutEffect(() => {
    if (!visible) return
    const update = () => {
      const el = anchorRef.current
      if (!el) return
      const r = el.getBoundingClientRect()
      setPos({ top: r.bottom + 4, left: r.left, width: r.width })
    }
    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [visible])

  useEffect(() => {
    if (!visible || active < 0) return
    document.getElementById(rowId(active))?.scrollIntoView?.({ block: 'nearest' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, visible])

  function close() {
    setOpen(false)
    setQuery('')
    setActive(-1)
  }

  const workScale = useWorkScaleClass()
  useComboboxEscape(visible, anchorRef, close)

  function commit(client: ClientSummary) {
    if (blurTimer.current) clearTimeout(blurTimer.current)
    close()
    setCommitted(client)
    onSelect(client)
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        if (!open) {
          setOpen(true)
          return
        }
        if (items.length > 0) setActive((a) => (a < 0 ? 0 : Math.min(a + 1, items.length - 1)))
        break
      case 'ArrowUp':
        e.preventDefault()
        if (open && items.length > 0) setActive((a) => (a <= 0 ? 0 : a - 1))
        break
      case 'Enter':
        if (open && active >= 0 && active < items.length) {
          e.preventDefault()
          commit(items[active])
        }
        break
      default:
        break
    }
  }

  return (
    <div ref={anchorRef}>
      <Input
        id={id}
        value={inputValue}
        placeholder={open && display ? display : placeholder}
        disabled={disabled}
        aria-label={aria['aria-label']}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={visible}
        aria-controls={visible ? listboxId : undefined}
        aria-activedescendant={visible && active >= 0 ? rowId(active) : undefined}
        onKeyDown={onKeyDown}
        onChange={(e) => {
          setQuery(e.target.value)
          if (!open) setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onBlur={() => {
          // Строгий выбор: набранный текст клиента не выбирает и журнал не грузит.
          blurTimer.current = setTimeout(close, 150)
        }}
      />
      {visible && pos
        ? createPortal(
            <ul
              id={listboxId}
              className={cn(
                'ds-surface pointer-events-auto fixed z-popover max-h-56 overflow-auto rounded-8 py-1 shadow-elevated',
                workScale,
              )}
              style={{ top: pos.top, left: pos.left, width: pos.width, pointerEvents: 'auto' }}
              role="listbox"
            >
              {error ? (
                <li className="px-3 py-2 text-13 text-red">{error}</li>
              ) : items.length > 0 ? (
                items.map((c, i) => (
                  // Клавиатура combobox'а живёт на инпуте (`aria-activedescendant`), опции по
                  // паттерну WAI-ARIA не фокусируются и своих keyboard-обработчиков не имеют.
                  // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                  <li
                    key={c.id}
                    id={rowId(i)}
                    role="option"
                    aria-selected={c.id === value}
                    className={cn(
                      'cursor-pointer px-3 py-2 text-14 text-txt hover:bg-hover',
                      i === active && 'bg-hover',
                    )}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => commit(c)}
                  >
                    <div>{c.name}</div>
                    {/* ИНН — второй строкой, менее заметным текстом; без ИНН строки нет. */}
                    {c.inn ? <div className="text-12 text-txt-tertiary">{c.inn}</div> : null}
                  </li>
                ))
              ) : loading ? (
                <li className="px-3 py-2 text-13 text-txt-tertiary">Загрузка…</li>
              ) : (
                <li className="px-3 py-2 text-13 text-txt-tertiary">{CLIENT_SELECT_EMPTY}</li>
              )}
            </ul>,
            document.body,
          )
        : null}
    </div>
  )
}
